import { useState, useEffect, useRef } from 'react';
import { Link } from 'react-router-dom';

const STORAGE_KEY = 'amm_cookie_consent';

function updateConsent(granted) {
  if (typeof window === 'undefined' || typeof window.gtag !== 'function') return;
  window.gtag('consent', 'update', {
    analytics_storage: granted ? 'granted' : 'denied',
    ad_storage: granted ? 'granted' : 'denied',
    ad_user_data: granted ? 'granted' : 'denied',
    ad_personalization: granted ? 'granted' : 'denied',
  });
}

// Google Consent Mode v2 banner. Defaults (analytics_storage / ad_storage) are
// set to "denied" in index.html before any gtag('config', ...) call runs, so
// no analytics/ad cookies are set until the visitor accepts here. This is
// required by Google for EEA/UK/Switzerland traffic and applies network-wide.
export default function CookieConsent() {
  const [visible, setVisible] = useState(false);
  const ref = useRef(null);

  useEffect(() => {
    let stored = null;
    try { stored = localStorage.getItem(STORAGE_KEY); } catch { /* ignore */ }
    if (!stored) setVisible(true);
  }, []);

  // Publish the banner's height as a CSS variable so anything else docked to the bottom of the screen
  // (the Ask widget, the mobile site switcher) can sit above it instead of underneath it. On a phone
  // this banner is tall enough to bury both of them until the visitor makes a choice.
  useEffect(() => {
    const root = document.documentElement;
    if (!visible) { root.style.setProperty('--bottom-bar-h', '0px'); return undefined; }
    const measure = () => root.style.setProperty('--bottom-bar-h', `${ref.current?.offsetHeight || 0}px`);
    measure();
    window.addEventListener('resize', measure);
    return () => { window.removeEventListener('resize', measure); root.style.setProperty('--bottom-bar-h', '0px'); };
  }, [visible]);

  const choose = (accepted) => {
    try { localStorage.setItem(STORAGE_KEY, accepted ? 'accepted' : 'declined'); } catch { /* ignore */ }
    updateConsent(accepted);
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      ref={ref}
      data-testid="cookie-consent-banner"
      className="fixed bottom-0 left-0 right-0 z-[9999] bg-gray-950 text-gray-200 border-t border-gray-800 px-4 py-4 sm:px-6"
    >
      <div className="max-w-5xl mx-auto flex flex-col sm:flex-row items-center gap-3 sm:gap-6">
        <p className="text-xs sm:text-sm text-gray-300 flex-1">
          We use cookies for analytics and, where enabled, personalized ads. Read our{' '}
          <Link to="/privacy" data-testid="cookie-consent-privacy-link" className="underline hover:text-white">
            Privacy Policy
          </Link>.
        </p>
        <div className="flex gap-2 shrink-0">
          <button
            type="button"
            data-testid="cookie-consent-decline-btn"
            onClick={() => choose(false)}
            className="text-xs sm:text-sm px-4 py-2 rounded-md border border-gray-700 text-gray-300 hover:bg-gray-900 transition-colors"
          >
            Decline
          </button>
          <button
            type="button"
            data-testid="cookie-consent-accept-btn"
            onClick={() => choose(true)}
            className="text-xs sm:text-sm px-4 py-2 rounded-md bg-white text-gray-950 font-medium hover:bg-gray-200 transition-colors"
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
